import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Shows a subtle "Install App" prompt on Android Chrome / Edge when the
 * beforeinstallprompt event fires. Stores user dismissal in localStorage so
 * we don't annoy returning visitors.
 */
export const PWAInstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissedAt = Number(localStorage.getItem("pwa-install-dismissed-at") || 0);
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    const recentlyDismissed = Date.now() - dismissedAt < sevenDays;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (!recentlyDismissed) {
        // Delay the banner so it doesn't compete with the hero load
        setTimeout(() => setVisible(true), 4000);
      }
    };

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.setItem("pwa-installed", "1");
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome !== "accepted") {
      localStorage.setItem("pwa-install-dismissed-at", String(Date.now()));
    }
    setDeferred(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-install-dismissed-at", String(Date.now()));
    setVisible(false);
  };

  if (!visible || !deferred) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 z-[60] md:left-auto md:right-6 md:bottom-6 md:max-w-sm"
    >
      <div className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white/95 p-4 shadow-2xl backdrop-blur">
        <img
          src="/pwa-icon-192.png"
          alt="MyEcoTour"
          className="h-10 w-10 flex-none rounded-lg"
        />
        <div className="flex-1">
          <p className="font-serif text-base text-stone-900">Install MyEcoTour</p>
          <p className="mt-0.5 text-xs leading-relaxed text-stone-600">
            Add to your home screen for instant access to curated journeys.
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              data-testid="pwa-install-accept"
              onClick={handleInstall}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#234032] px-3.5 py-1.5 text-xs font-medium text-white transition hover:bg-[#1a3127]"
            >
              <Download className="h-3.5 w-3.5" />
              Install
            </button>
            <button
              data-testid="pwa-install-dismiss"
              onClick={handleDismiss}
              className="rounded-full px-3 py-1.5 text-xs text-stone-500 transition hover:text-stone-900"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          aria-label="Close"
          onClick={handleDismiss}
          className="flex-none text-stone-400 transition hover:text-stone-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
